import { Toaster } from "@/components/ui/toaster";
import { lazy, Suspense, useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { RedirectToTasks, useAuth } from "@clerk/clerk-react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { OrgActivationGate } from "@/components/auth/OrgActivationGate";
import { ScopeProvider } from "@/contexts/ScopeContext";
import { PortfolioRedirect } from "@/components/layout/PortfolioRedirect";
import { Loader2 } from "lucide-react";
import { setTokenProvider } from "@/lib/apiClient";
import { SignalRProvider } from "@/contexts/SignalRContext";
import { queryClient } from "@/lib/queryClient";

// Eager imports — auth is the app entry (marketing site lives on the root domain)
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import SetupMfa from "./pages/SetupMfa";

// Lazy-loaded public / shared pages
const Report = lazy(() => import("./pages/Report"));
const ICShareablePage = lazy(() => import("./pages/ICShareablePage"));

// Lazy-loaded authenticated pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Profile = lazy(() => import("./pages/Profile"));
const NotificationCenter = lazy(() => import("./pages/NotificationCenter"));

// SaaS re-platform: org-native self-serve app
const ReposPage = lazy(() => import("./pages/app/ReposPage"));
const AnalysisReportPage = lazy(() => import("./pages/app/AnalysisReportPage"));
const BoardSummaryPage = lazy(() => import("./pages/app/BoardSummaryPage"));
const ConnectionsPage = lazy(() => import("./pages/app/ConnectionsPage"));
const BillingPage = lazy(() => import("./pages/app/BillingPage"));
const OrganizationTeamPage = lazy(() => import("./pages/app/OrganizationTeamPage"));
const DashboardPage = lazy(() => import("./pages/app/DashboardPage"));
const OrgDigestPage = lazy(() => import("./pages/app/OrgDigestPage"));
const ReadinessPage = lazy(() => import("./pages/app/ReadinessPage"));
const SellSideReadinessPage = lazy(() => import("./pages/app/SellSideReadinessPage"));
const PortfolioCompanyPage = lazy(() => import("./pages/app/PortfolioCompanyPage"));
const SharedAnalysisPage = lazy(() => import("./pages/app/SharedAnalysisPage"));
const SharingPage = lazy(() => import("./pages/app/SharingPage"));
const DataRoomsPage = lazy(() => import("./pages/app/DataRoomsPage"));
const DataRoomPage = lazy(() => import("./pages/app/DataRoomPage"));
const DiligencePage = lazy(() => import("./pages/app/DiligencePage"));
const SharedDataRoomPage = lazy(() => import("./pages/app/SharedDataRoomPage"));
const AcceptDataRoomInvitePage = lazy(() => import("./pages/app/AcceptDataRoomInvitePage"));
const InvitePage = lazy(() => import("./pages/InvitePage"));

// Lazy-loaded admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const BulkImport = lazy(() => import("./pages/admin/BulkImport"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const SecurityDashboard = lazy(() => import("./pages/admin/SecurityDashboard"));
const AuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const FindingTracker = lazy(() => import("./pages/admin/FindingTracker"));
const Organizations = lazy(() => import("./pages/admin/Organizations"));
const CEOManagement = lazy(() => import("./pages/admin/CEOManagement"));
const ScheduledScansManagement = lazy(() => import("./pages/admin/ScheduledScansManagement"));
const Diagnostics = lazy(() => import("./pages/admin/Diagnostics"));
const RateLimitConfig = lazy(() => import("./pages/admin/RateLimitConfig"));

const ReportExport = lazy(() => import("./pages/ReportExport"));

// Provenance platform console (control plane — platform staff only)
const PlatformOverview = lazy(() => import("./pages/platform/PlatformOverview"));
const PlatformClients = lazy(() => import("./pages/platform/PlatformClients"));
const PlatformClientDetail = lazy(() => import("./pages/platform/PlatformClientDetail"));
const PlatformUsers = lazy(() => import("./pages/platform/PlatformUsers"));
const PlatformRuns = lazy(() => import("./pages/platform/PlatformRuns"));
const PlatformTokens = lazy(() => import("./pages/platform/PlatformTokens"));
const PlatformOps = lazy(() => import("./pages/platform/PlatformOps"));
const PlatformAI = lazy(() => import("./pages/platform/PlatformAI"));
const PlatformReplay = lazy(() => import("./pages/platform/PlatformReplay"));
const PlatformAudit = lazy(() => import("./pages/platform/PlatformAudit"));

const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

/**
 * Registers the Clerk getToken function with apiClient
 * so all API calls include the JWT automatically.
 */
function TokenProviderSetup() {
  const { getToken, orgId } = useAuth();
  useEffect(() => {
    setTokenProvider((opts) => getToken({
      ...opts,
      organizationId: opts?.organizationId ?? orgId ?? undefined,
    }));
  }, [getToken, orgId]);
  return null;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TokenProviderSetup />
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <SignalRProvider>
        <ScopeProvider>
        <BrowserRouter>
          <RedirectToTasks />
          <OrgActivationGate />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
                {/* Auth is the app entry (marketing site lives on the root domain) */}
                <Route path="/" element={<Auth />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/sign-in" element={<Auth />} />
                <Route path="/sign-up" element={<Auth />} />
                <Route path="/signup" element={<Auth />} />
                <Route path="/session-tasks/setup-mfa" element={<SetupMfa />} />
                <Route path="/invite/:token" element={<InvitePage />} />

                {/* Public / shared */}
                <Route path="/report/:id" element={<Report />} />
                <Route path="/report/:id/ic-summary" element={<ICShareablePage />} />
                <Route path="/report/:id/export" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><ReportExport /></ProtectedRoute>} />


                {/* Authenticated routes (any role) */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

                {/* Org-native self-serve app (SaaS re-platform) */}
                <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                <Route path="/app" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="/app/portfolio" element={<ProtectedRoute><PortfolioRedirect /></ProtectedRoute>} />
                <Route path="/app/portfolio/:id" element={<ProtectedRoute><PortfolioCompanyPage /></ProtectedRoute>} />
                <Route path="/app/portfolio/:companyId/shared/:analysisId" element={<ProtectedRoute><SharedAnalysisPage /></ProtectedRoute>} />
                <Route path="/app/repos" element={<ProtectedRoute><ReposPage /></ProtectedRoute>} />
                <Route path="/app/readiness" element={<ProtectedRoute><ReadinessPage /></ProtectedRoute>} />
                <Route path="/app/digest" element={<ProtectedRoute><OrgDigestPage /></ProtectedRoute>} />
                <Route path="/app/sharing" element={<ProtectedRoute><SharingPage /></ProtectedRoute>} />
                <Route path="/app/diligence" element={<ProtectedRoute><DiligencePage /></ProtectedRoute>} />
                <Route path="/app/data-rooms" element={<ProtectedRoute><DataRoomsPage /></ProtectedRoute>} />
                <Route path="/app/data-rooms/:id" element={<ProtectedRoute><DataRoomPage /></ProtectedRoute>} />
                <Route path="/app/shared-data-rooms/:id" element={<ProtectedRoute><SharedDataRoomPage /></ProtectedRoute>} />
                <Route path="/app/data-room-invite/:token" element={<ProtectedRoute><AcceptDataRoomInvitePage /></ProtectedRoute>} />
                <Route path="/app/repos/:repoId/analyses/:analysisId" element={<ProtectedRoute><AnalysisReportPage /></ProtectedRoute>} />
                <Route path="/app/repos/:repoId/analyses/:analysisId/readiness" element={<ProtectedRoute><SellSideReadinessPage /></ProtectedRoute>} />
                <Route path="/app/repos/:repoId/analyses/:analysisId/summary" element={<ProtectedRoute><BoardSummaryPage /></ProtectedRoute>} />
                <Route path="/app/repos/:repoId/ask" element={<ProtectedRoute><Navigate to="/app/data-rooms" replace /></ProtectedRoute>} />
                <Route path="/app/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
                <Route path="/app/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
                <Route path="/app/team" element={<ProtectedRoute><OrganizationTeamPage /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><NotificationCenter /></ProtectedRoute>} />

                {/* Admin/Internal routes */}
                <Route path="/admin" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['internal_admin']}><AdminSettings /></ProtectedRoute>} />
                <Route path="/admin/bulk-import" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><BulkImport /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['internal_admin']}><UserManagement /></ProtectedRoute>} />
                <Route path="/admin/security" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><SecurityDashboard /></ProtectedRoute>} />
                <Route path="/admin/audit-logs" element={<ProtectedRoute allowedRoles={['internal_admin']}><AuditLogs /></ProtectedRoute>} />
                <Route path="/admin/findings" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><FindingTracker /></ProtectedRoute>} />
                <Route path="/admin/organizations" element={<ProtectedRoute allowedRoles={['internal_admin']}><Organizations /></ProtectedRoute>} />
                <Route path="/admin/ceo-management" element={<ProtectedRoute allowedRoles={['internal_admin']}><CEOManagement /></ProtectedRoute>} />
                <Route path="/admin/scheduled-scans" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><ScheduledScansManagement /></ProtectedRoute>} />
                <Route path="/admin/rate-limits" element={<ProtectedRoute allowedRoles={['internal_admin']}><RateLimitConfig /></ProtectedRoute>} />
                <Route path="/diagnostics" element={<ProtectedRoute allowedRoles={['internal_admin']}><Diagnostics /></ProtectedRoute>} />

                {/* Provenance platform console (control plane — platform staff only) */}
                <Route path="/platform" element={<ProtectedRoute allowedRoles={['platform_admin', 'platform_support']}><PlatformOverview /></ProtectedRoute>} />
                <Route path="/platform/clients" element={<ProtectedRoute allowedRoles={['platform_admin', 'platform_support']}><PlatformClients /></ProtectedRoute>} />
                <Route path="/platform/clients/:id" element={<ProtectedRoute allowedRoles={['platform_admin', 'platform_support']}><PlatformClientDetail /></ProtectedRoute>} />
                <Route path="/platform/users" element={<ProtectedRoute allowedRoles={['platform_admin']}><PlatformUsers /></ProtectedRoute>} />
                <Route path="/platform/runs" element={<ProtectedRoute allowedRoles={['platform_admin', 'platform_support']}><PlatformRuns /></ProtectedRoute>} />
                <Route path="/platform/tokens" element={<ProtectedRoute allowedRoles={['platform_admin', 'platform_support']}><PlatformTokens /></ProtectedRoute>} />
                <Route path="/platform/ai" element={<ProtectedRoute allowedRoles={['platform_admin', 'platform_support']}><PlatformAI /></ProtectedRoute>} />
                <Route path="/platform/replay" element={<ProtectedRoute allowedRoles={['platform_admin', 'platform_support']}><PlatformReplay /></ProtectedRoute>} />
                <Route path="/platform/audit" element={<ProtectedRoute allowedRoles={['platform_admin', 'platform_support']}><PlatformAudit /></ProtectedRoute>} />
                <Route path="/platform/system" element={<ProtectedRoute allowedRoles={['platform_admin', 'platform_support']}><PlatformOps /></ProtectedRoute>} />
                {/* Consolidated: Ops + Diagnostics → System */}
                <Route path="/platform/ops" element={<Navigate to="/platform/system" replace />} />
                <Route path="/platform/diagnostics" element={<Navigate to="/platform/system" replace />} />

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ScopeProvider>
        </SignalRProvider>
        </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
